import { Heading, List, Paragraph, Spacer } from '@zillow/constellation';
import { Group } from '@zillow/zrs-cms';
import Head from 'next/head';
import React from 'react';

import { REVALIDATION_INTERVAL } from '../../server/config';
import GlobalStyle from '../components/GlobalStyle';
import { ZestimateData } from '../components/ZestimateData/ZestimateData';
import {
    ZESTIMATE_DATA_OFF_MARKET,
    ZESTIMATE_DATA_ON_MARKET,
} from '../components/ZestimateData/ZestimateData.data';
import { ZestimateFAQ } from '../components/ZestimateFAQ/ZestimateFAQ';

export interface ZestimateProps {
    useVisualRefreshTheme?: boolean;
}

/**
 * Zestimate page component that explains Zillow's home valuation model.
 *
 * @component
 */
export function Zestimate({
    useVisualRefreshTheme: _useVisualRefreshTheme,
}: ZestimateProps): JSX.Element {
    return (
        <>
            <Head>
                <title>What is a Zestimate? | Zillow</title>
                <meta
                    name="description"
                    content="The Zestimate® home valuation model is Zillow's estimate of a home's market value. Learn about how the Zestimate is calculated and its accuracy."
                />
            </Head>
            <GlobalStyle />
            {/* @ts-expect-error - Group component uses PropTypes, not TypeScript types */}
            <Group
                maxWidth={{ default: 'full' }}
                backgroundColor="gray100"
                paddingTop={{ default: 'xl' }}
                paddingBottom={{ default: 'xl' }}
                paddingRight={{ default: 'xl' }}
                paddingLeft={{ default: 'xl' }}
            >
                {/* @ts-expect-error - Group component uses PropTypes, not TypeScript types */}
                <Group maxWidth={{ default: 'md' }} alignItems="stretch">
                    <Heading
                        level="1"
                        fontType={{ default: 'h2', md_lte: 'h3' }}
                        fontFamily="serif"
                        marginBottom="sm"
                        textAlign="center"
                    >
                        What is a Zestimate?
                    </Heading>
                    <Paragraph
                        fontSize="large"
                        color="textMedium"
                        textAlign="center"
                    >
                        The Zestimate® home valuation model is Zillow&apos;s
                        estimate of a home&apos;s market value. A Zestimate
                        incorporates public, MLS and user-submitted data into
                        Zillow&apos;s proprietary formula, also taking into
                        account home facts, location and market trends. It is
                        not an appraisal and can&apos;t be used in place of an
                        appraisal.
                    </Paragraph>
                </Group>
            </Group>
            {/* @ts-expect-error - Group component uses PropTypes, not TypeScript types */}
            <Group
                maxWidth={{ default: 'md' }}
                paddingTop={{ default: 'md' }}
                paddingBottom={{ default: 'md' }}
                paddingRight={{ default: 'md' }}
                paddingLeft={{ default: 'md' }}
                alignItems="stretch"
            >
                <Heading
                    level="2"
                    fontType="h3"
                    fontFamily="serif"
                    marginBottom="md"
                    textAlign="center"
                >
                    How accurate is the Zestimate?
                </Heading>
                <Paragraph marginBottom="md" textAlign="left">
                    The nationwide median error rate for the Zestimate for
                    on-market homes is 1.83%, while the Zestimate for off-market
                    homes has a median error rate of 7.01%. The Zestimate&apos;s
                    accuracy depends on the availability of data in a
                    home&apos;s area. Some areas have more detailed home
                    information available — such as square footage and number of
                    bedrooms or bathrooms — and others do not. The more data
                    available, the more accurate the Zestimate value will be.
                </Paragraph>
                <Paragraph marginBottom="md" textAlign="left">
                    These tables break down the accuracy of Zestimates for both
                    active listings and off-market homes by region.
                </Paragraph>
            </Group>
            {/* @ts-expect-error - Group component uses PropTypes, not TypeScript types */}
            <Group maxWidth={{ default: 'xl' }} alignItems="stretch">
                <ZestimateData
                    data={ZESTIMATE_DATA_ON_MARKET}
                    title="Active listings accuracy"
                />
                <ZestimateData
                    data={ZESTIMATE_DATA_OFF_MARKET}
                    title="Off-market homes accuracy"
                />
            </Group>
            {/* @ts-expect-error - Group component uses PropTypes, not TypeScript types */}
            <Group
                maxWidth={{ default: 'md' }}
                paddingTop={{ default: 'md' }}
                paddingBottom={{ default: 'md' }}
                paddingRight={{ default: 'md' }}
                paddingLeft={{ default: 'md' }}
                alignItems="stretch"
            >
                <Heading
                    level="4"
                    fontType="h4"
                    fontFamily="serif"
                    marginBottom="md"
                    textAlign="center"
                >
                    How is the Zestimate calculated?
                </Heading>
                <Paragraph marginBottom="md">
                    Zillow publishes Zestimate home valuations for 116 million
                    homes across the country, and uses state of the art
                    statistical and machine learning models that can examine
                    hundreds of data points for each individual home.
                </Paragraph>
                <Paragraph marginBottom="md">
                    To calculate a Zestimate, Zillow uses a sophisticated neural
                    network-based model that incorporates data from county and
                    tax assessor records and direct feeds from hundreds of
                    multiple listing services and brokerages. The Zestimate also
                    incorporates:
                </Paragraph>
                <List>
                    <List.Item>
                        Home characteristics including square footage, location
                        or the number of bathrooms.
                    </List.Item>
                    <List.Item>
                        On-market data such as listing price, description,
                        comparable homes in the area and days on the market
                    </List.Item>
                    <List.Item>
                        Off-market data — tax assessments, prior sales and other
                        publicly available records
                    </List.Item>
                    <List.Item>
                        Market trends, including seasonal changes in demand
                    </List.Item>
                </List>
                <Paragraph marginTop="md">
                    Currently, we have data for over 124 million U.S. homes and
                    we publish Zestimates for 116 million of them.
                </Paragraph>
            </Group>
            {/* @ts-expect-error - Group component uses PropTypes, not TypeScript types */}
            <Group
                maxWidth={{ default: 'md' }}
                paddingTop={{ default: 'md' }}
                paddingBottom={{ default: 'md' }}
                paddingRight={{ default: 'md' }}
                paddingLeft={{ default: 'md' }}
                alignItems="stretch"
            >
                <Heading
                    level="5"
                    fontType="h4"
                    fontFamily="serif"
                    marginBottom="md"
                    textAlign="center"
                >
                    FAQ
                </Heading>
                <ZestimateFAQ />
                <Spacer marginBottom="md" />
                <List appearance="none">
                    <List.Item>
                        <Paragraph fontType="legal">
                            * The nationwide median error rate for the Zestimate
                            for on-market homes means that half of all on-market
                            homes are within the median error vs. the selling
                            price, and half are not. For most major markets, the
                            Zestimate for on-market homes is within 10% of the
                            final sale price more than 95% of the time.
                        </Paragraph>
                    </List.Item>
                    <List.Item>
                        <Paragraph fontType="legal">
                            ** We can only calculate Zestimates for homes and
                            regions where we have certain data, including home
                            facts and local, historical sales transactions. This
                            column indicates the number of homes in an area that
                            have Zestimates.
                        </Paragraph>
                    </List.Item>
                </List>
            </Group>
        </>
    );
}

/**
 * Generates static props for the Zestimate page.
 * Uses Incremental Static Regeneration (ISR) to update the page periodically.
 *
 * @returns Static props for the page with Visual Refresh theme enabled
 */
export async function getStaticProps() {
    return {
        props: {
            useVisualRefreshTheme: true,
        },
        revalidate: REVALIDATION_INTERVAL,
    };
}

export default Zestimate;
